<template>
  <div class="archive-page">
    <!-- Page Header with Title -->
    <div class="page-header py-6 px-10">
      <div class="page-title-section">
        <h1 class="page-title">Archive</h1>
        <p class="page-subtitle">Access and manage your archived items</p>
      </div>
    </div>

    <div class="archive-top-tabs px-10 mb-[2px]">
      <Tab v-model="activeTab" type="outlined" :tabs="archiveTabs" />
    </div>

    <!-- Content Area with White Background -->
    <div class="content-area -mr-5 -ml-5 px-10">
      <!-- eZ-Bill Archive Content -->
      <div v-if="activeTab === 'ez-bill'" class="archive-content">
        <div class="content-wrapper">
          <!-- Sub Tabs -->
          <div class="sub-tabs">
            <Tab v-model="activeSubTab" type="underline" :tabs="archiveSubTabs" />
          </div>

          <!-- Sub-components for eZ-Bill tabs -->
          <ProgramsArchive v-if="activeSubTab === 'programs'" />
          <SubscriptionsArchive v-if="activeSubTab === 'subscriptions'" />
          <PracticeProductsArchive 
            v-if="activeSubTab === 'practice-products'"
            :customer-id="customerId"
            :clinic-id="clinicId"
          />
          <ProductBundleArchive
            v-if="activeSubTab === 'product-bundle'"
            :customer-id="customerId"
            :clinic-id="clinicId"
            @page-change="handlePageChange"
            @page-size-change="handlePageSizeChange"
          />
        </div>
      </div>

      <!-- Document Templates Archive Content -->
      <DocumentTemplatesArchive v-else-if="activeTab === 'document-templates'" />
      <!-- Automation Archive Content -->
      <AutomationArchive v-else-if="activeTab === 'automation'" />
      <!-- Files Tab - Coming Soon -->
      <div v-else-if="activeTab === 'files'" class="content-body">
        <div class="coming-soon-state">
          <p>{{ activeTabDisplay }} archive coming soon...</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import EzBillIcon from '@/assets/MyWorkBench/archive/ez_bill.svg';
import DocumentTemplateIcon from '@/assets/MyWorkBench/archive/document_template.svg';
import AutomationIcon from '@/assets/MyWorkBench/archive/automation.svg';
import ProgramIcon from '@/assets/MyWorkBench/archive/program.svg';
import PracticeProductIcon from '@/assets/MyWorkBench/archive/practice-product.svg';
import ProductBundleIcon from '@/assets/MyWorkBench/archive/product-bundle.svg';
import ProgramsArchive from './ez-bill/ProgramsArchive.vue';
import SubscriptionsArchive from './ez-bill/SubscriptionsArchive.vue';
import PracticeProductsArchive from './ez-bill/PracticeProductsArchive.vue';
import ProductBundleArchive from './ez-bill/ProductBundleArchive.vue';
import DocumentTemplatesArchive from './DocumentTemplatesArchive.vue';
import AutomationArchive from './AutomationArchive.vue';

export default {
  name: 'Archive',
  components: {
    ProgramsArchive,
    SubscriptionsArchive,
    PracticeProductsArchive,
    ProductBundleArchive,
    DocumentTemplatesArchive,
    AutomationArchive,
  },

  data() {
    return {
      activeTab: 'ez-bill',
      activeSubTab: 'programs',
    };
  },

  computed: {
    archiveTabs() {
      return [
        { value: 'ez-bill', label: 'eZ-Bill', icon: EzBillIcon },
        { value: 'document-templates', label: 'Document Templates', icon: DocumentTemplateIcon },
        { value: 'automation', label: 'Automation', icon: AutomationIcon },
      ];
    },

    archiveSubTabs() {
      return [
        { value: 'programs', label: 'Programs', icon: ProgramIcon },
        { value: 'practice-products', label: 'Practice Products', icon: PracticeProductIcon },
        { value: 'product-bundle', label: 'Product Bundle', icon: ProductBundleIcon },
      ];
    },

    activeTabDisplay() {
      const displayNames = {
        'ez-bill': 'eZ-Bill',
        'document-templates': 'Document Templates',
        'automation': 'Automation',
        'files': 'Files'
      };
      return displayNames[this.activeTab] || 'Items';
    },

    // Get customer ID from store userInfo
    customerId() {
      return this.$store?.getters['login/userInfo']?.customer_id || null;
    },

    // Get clinic ID from store userInfo
    clinicId() {
      return this.$store?.getters['login/userInfo']?.clinic_id || null;
    },
  },

  methods: {
    handlePageChange(page) {
      // Handled by sub-components via events
    },
    handlePageSizeChange(size) {
      // Handled by sub-components via events
    },
  },
};
</script>

<style scoped>
.archive-page {
  padding: 0;
  background: transparent;
  height: 100%;
  width: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

/* Page Header */
.page-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  background: transparent;
}

.page-title-section {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
}

.page-title {
  font-family: 'Avenir', sans-serif;
  font-size: 22px;
  font-weight: 500;
  line-height: 24px;
  color: #333;
  margin: 0;
  white-space: nowrap;
}

.page-subtitle {
  font-family: 'Inter', sans-serif;
  font-size: 16px;
  font-weight: 400;
  line-height: 24px;
  letter-spacing: 0.5px;
  color: #666;
  margin: 0;
}

/* Content Area */
.content-area {
  background: #FFFFFF;
  margin: 0;
  box-shadow: 0px 1px 15px 0px rgba(164, 164, 164, 0.25);
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  overflow: hidden;
  border-radius: 5px 5px 0 0;
}

.archive-top-tabs :deep(.va-tab-group__main) {
  display: flex;
  gap: 8px;
  width: 100%;
}

.archive-top-tabs :deep(.va-tab) {
  display: flex;
  align-items: center;
  gap: 10px;
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  letter-spacing: 0.1px;
  color: #666;
  background-color: #fff;
  padding: 10px 12px;
  border-bottom: 2px solid transparent;
  transition: all 0.2s ease;
  position: relative;
  bottom: -1px;
  box-shadow: 0 -4px 6px rgba(164, 164, 164, 0.25), 4px 0 6px rgba(164, 164, 164, 0.25), -4px 0 6px rgba(164, 164, 164, 0.25);
  border-top-left-radius: 4px;
  border-top-right-radius: 4px;
}

.archive-top-tabs :deep(.va-tab:hover) {
  color: #004879;
}

.archive-top-tabs :deep(.va-tab--active) {
  color: #004879;
  font-weight: 700;
  border-bottom-color: #004879;
  background-color: #F9FBFD;
  box-shadow: -1px -1px 15px 0px rgba(164, 164, 164, 0.25), 1px -1px 15px 0px rgba(164, 164, 164, 0.25);
  border-left: 1px solid #004879;
  border-right: 1px solid #004879;
  border-top: 1px solid #004879;
}

/* Archive Content */
.archive-content {
  padding: 0;
  flex: 1;
  min-height: 0;
  position: relative;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.content-wrapper {
  display: flex;
  flex-direction: column;
  gap: 16px;
  flex: 1;
  height: 100%;
  padding: 24px 0 24px 0;
  min-height: 0;
  overflow: hidden;
}

/* Sub Tabs */
.sub-tabs {
  border-bottom: 1px solid #E6E6E6;
  flex-shrink: 0;
}

.sub-tabs :deep(.va-tab-group__main) {
  display: flex;
  width: 100%;
}

.sub-tabs :deep(.va-tab) {
  flex: 1;
  min-width: 0;
  justify-content: center;
}

/* Content Body */
.content-body {
  padding: 0 12px;
  flex: 1;
  overflow-y: auto;
}

/* Coming Soon State */
.coming-soon-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 100%;
  padding: 40px 20px;
  text-align: center;
}

.coming-soon-state p {
  font-family: 'Inter', sans-serif;
  font-size: 16px;
  color: #333;
  margin: 0;
}
</style>
